import { RadarCompanyTable } from './RadarCompanyTable';
import { observeVisible, radarAnalytics } from './radarAnalytics';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import { useRadarDisplay } from './RadarDisplay';
import { RadarLocalizedField } from './RadarLocalizedField';
import { RADAR_LOCALES } from '@/i18n/radarLanding';
import { useEffect, useRef, useState, type ReactNode, type TextareaHTMLAttributes } from 'react';
import {
  ArrowUpRight,
  BookOpenText,
  Building2,
  Check,
  ChevronRight,
  CircleDot,
  Compass,
  LoaderCircle,
  RotateCcw,
  Search,
  Sparkles,
} from 'lucide-react';
import type {
  MarketBrief,
  RadarCandidate,
  SearchStatus,
  RadarWorkspaceBundle,
} from './types';
import '@/styles/radar-workspace.css';
import { useOutreachFlow, preparationReasons } from './RadarOutreachFlow';
import { RadarWatchPanel } from './RadarWatchPanel';
import { RadarUsageDrawer, RadarUsageEntry, RadarUsageInline } from './RadarUsage';
import { RadarQuickStartLink } from './RadarQuickStartLink';
import type { RadarUsageContext } from './radarUsageModel';
import type { RadarWatch, SignalPlan, SignalFocus } from './types';

type FeedbackVerdict = 'relevant' | 'not_fit';

type Props = {
  workspaceId?: string;
  currentSearchRunId?: string;
  actPanel?: ReactNode;
  briefPanel?: ReactNode;
  versionFilter?: ReactNode;
  researchPanel?: ReactNode;
  monitorPanel?: ReactNode;
  onActContact?: (id: string) => void;
  watchFocusId?: string;
  merchantName: string;
  brief: MarketBrief;
  briefStatus: 'needs_review' | 'confirmed';
  activeSection?: 'brief' | 'discover' | 'watch' | 'act';
  onSectionChange?: (section: 'brief' | 'discover' | 'watch' | 'act') => void;
  hasUnsavedBriefChanges?: boolean;
  onDiscardBrief?: () => void;
  candidates: RadarCandidate[];
  watches?: RadarWatch[];
  watchSummary?: { active_count: number; limit: number };
  signalPlan?: SignalPlan | null;
  savingWatch?: boolean;
  onWatch?: (candidateId: string) => void;
  onWatchConfirm?: (ids: string[]) => void;
  onSignalFeedback?: (id: string, verdict: 'useful' | 'not_relevant') => void;
  onWatchStatus?: (id: string, status: 'active' | 'paused') => void;
  onFocus?: (focus: SignalFocus) => void;
  searchStatus: SearchStatus;
  searchError?: string | null;
  assumptions?: string[];
  reviewCriteria?: boolean;
  searchesRemaining?: number;
  usage?: RadarUsageContext;
  confirming?: boolean;
  startingSearch?: boolean;
  connectionNotice?: string;
  requestError?: string;
  feedbackError?: string;
  savingFeedback?: boolean;
  onReconnect?: () => void;
  discoverySummary?: RadarWorkspaceBundle['discovery_summary'];
  onBriefChange: (brief: MarketBrief) => void;
  onConfirmBrief: () => void;
  onSearch: () => void;
  onFeedback: (
    candidateId: string,
    verdict: FeedbackVerdict,
    reasons: string[],
    note?: string
  ) => void;
  onUndoFeedback: (candidateId: string) => void;
};

const relevantReasons = [
  ['right_buyer_type', 'Right kind of buyer'],
  ['right_market', 'Right market'],
  ['strong_capabilities', 'Strong channel capabilities'],
  ['good_timing', 'Good timing'],
] as const;

const notFitReasons = [
  ['wrong_buyer_type', 'Wrong kind of buyer'],
  ['wrong_market', 'Wrong market'],
  ['wrong_channel', 'Cannot sell through this channel'],
  ['too_large', 'Too large for us'],
  ['already_customer', 'Already a customer'],
  ['weak_evidence', 'Evidence is too weak'],
] as const;

export function GrowingTextarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const field = ref.current;
    if (!field) return;
    const resize = () => {
      field.style.height = 'auto';
      field.style.height = `${field.scrollHeight + 2}px`;
    };
    resize();
    // Also resize when a closed assumptions panel opens or the viewport changes.
    let width = field.clientWidth;
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      if (field.clientWidth !== width) { width = field.clientWidth; resize(); }
    });
    observer?.observe(field);
    return () => observer?.disconnect();
  }, [props.value]);
  return <textarea {...props} ref={ref} rows={2} className="radar-growing-field" />;
}

function BriefListField({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  // Keep raw lines in the parent draft, including unfinished lines while typing.
  // Normalize at submission so confirmation never depends on a blur event.
  return <RadarLocalizedField value={value} onChange={onChange}/>;
}

function BriefEditor({
  brief,
  assumptions = [],
  onChange,
  onConfirm,
  confirming = false,
  confirmed = false,
  onDiscard,
  reviewCriteria,
}: {
  brief: MarketBrief;
  assumptions?: string[];
  onChange: (brief: MarketBrief) => void;
  onConfirm: () => void;
  confirming?: boolean;
  confirmed?: boolean;
  onDiscard?: () => void;
  reviewCriteria?: boolean;
}) {
  const { t } = useRadarLocale();
  const { d } = useRadarDisplay();
  return (
    <section className="radar-workspace-panel radar-brief-panel">
      <div className="radar-workspace-eyebrow">{t("Market Brief ·")}{' '}{confirmed ? t("your direction") : t("hypothesis")}</div>
      <div className="radar-brief-heading">
        <div>
          <h1>{confirmed ? t("Your Market Brief") : t("This is what we understand about your growth target. Is it right?")}</h1>
          <p>
            {confirmed ? t("Save changes for your next search. Your existing companies and feedback stay in Discover.")
              : t("This is a starting point, not a verdict. Correct it before the radar looks for companies.")}
          </p>
        </div>
        {!confirmed && <span className="radar-review-stamp">{t("Needs your review")}</span>}
      </div>

      <fieldset disabled={confirming} className="radar-brief-fields">
      <div className="radar-brief-grid">
        <label className="radar-field radar-field-wide">
          <span>{t("What do you sell?")}</span>
          <small>{t("Describe the offer a buyer would actually evaluate.")}</small>
          <RadarLocalizedField value={brief.seller_summary}
            onChange={(seller_summary) => onChange({ ...brief, seller_summary })}/>
        </label>
        <label className="radar-field">
          <span>{t("Where do you want to grow?")}</span>
          <small>{t("One focused market is enough for the first scan.")}</small>
          <RadarLocalizedField singleLine value={brief.target_market}
            onChange={(target_market) => onChange({ ...brief, target_market })}/>
        </label>
        <label className="radar-field">
          <span>{t("Who do you need to win first?")}</span>
          <small>{t("For example: a distributor with hospital access.")}</small>
          <BriefListField value={brief.target_account_types} onChange={(value) => onChange({ ...brief, target_account_types: value })} />
        </label>
        <label className="radar-field radar-field-wide">
          <span>{t("What relationship are you trying to build?")}</span>
          <RadarLocalizedField value={brief.relationship_goal}
            onChange={(relationship_goal) => onChange({ ...brief, relationship_goal })}/>
        </label>
      </div>

      <details className="radar-brief-details" open={reviewCriteria || undefined}>
        <summary>{t("Review search assumptions")}</summary>
        <div className="radar-brief-grid radar-brief-grid-advanced">
          <label className="radar-field">
            <span>{t("Products")}</span>
            <BriefListField value={brief.products} onChange={(value) => onChange({ ...brief, products: value })} />
          </label>
          <label className="radar-field">
            <span>{t("Required capabilities")}</span>
            <BriefListField value={brief.required_capabilities} onChange={(value) => onChange({ ...brief, required_capabilities: value })} />
          </label>
          <label className="radar-field">
            <span>{t("Exclude")}</span>
            <BriefListField value={brief.exclusions} onChange={(value) => onChange({ ...brief, exclusions: value })} />
          </label>
          <label className="radar-field">
            <span>{t("Signals to look for")}</span>
            <BriefListField value={brief.trigger_types} onChange={(value) => onChange({ ...brief, trigger_types: value })} />
          </label>
        </div>
      </details>
      </fieldset>

      {assumptions.length > 0 && (
        <aside className="radar-assumption-note">
          <Sparkles size={16} />
          <div>
            <strong>{t("Please pressure-test these assumptions")}</strong>
            <ul>{assumptions.map((item) => <li key={item}>{d(item)}</li>)}</ul>
          </div>
        </aside>
      )}

      <div className="radar-panel-action">
        <button className="radar-primary-action" type="button" onClick={onConfirm} disabled={confirming} aria-busy={confirming}>
          {confirming ? <><LoaderCircle className="spin" size={18} />{' '}{t("Saving your Market Brief…")}</>
            : confirmed ? t("Save changes") : <>{t("Confirm and build my radar")}{' '}<ChevronRight size={18} /></>}
        </button>
        {confirmed && onDiscard && <button type="button" className="radar-secondary-action" onClick={onDiscard} disabled={confirming}>{t("Discard changes")}</button>}
        <span>{confirmed ? t("Saving does not use a search.") : t("Your brief stays editable after confirmation.")}</span>
      </div>
    </section>
  );
}

function EvidenceLink({ evidence, candidateId, workspaceId }: { evidence: RadarCandidate['fit_evidence'][number]; candidateId?: string; workspaceId?: string }) {
  const { d } = useRadarDisplay();
  return (
    <a className="radar-evidence-link" onClick={() => radarAnalytics.capture('radar_item_opened', { item_type: 'evidence', item_id: candidateId, workspace_id: workspaceId })} href={evidence.url} target="_blank" rel="noreferrer">
      <span>{d(evidence.title)}</span>
      <small>{evidence.published_date || new URL(evidence.url).hostname}</small>
      <ArrowUpRight size={14} />
    </a>
  );
}

function CandidateCard({
  workspaceId,
  candidate,
  onFeedback,
  onUndo,
  onWatch,
  outreachAction,
  busy = false,
}: {
  workspaceId?: string;
  candidate: RadarCandidate;
  outreachAction?: ReactNode;
  onFeedback: Props['onFeedback'];
  onUndo: Props['onUndoFeedback'];
  onWatch?: Props['onWatch'];
  busy?: boolean;
}) {
  const { t } = useRadarLocale();
  const { d } = useRadarDisplay();
  const [composer, setComposer] = useState<FeedbackVerdict | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const isMoment = candidate.classification === 'buyer_moment';
  const triggerEvidence = candidate.trigger?.evidence || candidate.trigger_evidence;
  const reasonOptions = composer === 'relevant' ? relevantReasons : notFitReasons;
  const evidenceLinks = candidate.fit_evidence.filter((evidence, index, all) => all.findIndex((item) => item.url === evidence.url) === index);

  function choose(verdict: FeedbackVerdict) {
    setComposer(verdict);
    setReasons([]);
    setNote('');
  }

  function toggleReason(reason: string) {
    setReasons((current) =>
      current.includes(reason)
        ? current.filter((item) => item !== reason)
        : [...current, reason]
    );
  }

  return (
    <article className={`radar-candidate-card ${isMoment ? 'is-moment' : ''}`}>
      <header className="radar-candidate-head">
        <div className="radar-rank">{String(candidate.rank).padStart(2, '0')}</div>
        <div className="radar-candidate-identity">
          <span className={`radar-result-type ${isMoment ? 'is-moment' : ''}`}>
            {isMoment ? t("Buyer Moment") : t("Fit Candidate")}
          </span>
          {candidate.discovery_status && <span className="radar-discovery-label">
            {{ new: t("New this search"), seen_again: t("Found again"), previous: t("From an earlier search") }[candidate.discovery_status]}
          </span>}
          <h2>{candidate.company_name}</h2>
          <p>{d(candidate.buyer_type)} · {d(candidate.country)}</p>
        </div>
        <a
          className="radar-company-link"
          onClick={() => radarAnalytics.capture('radar_item_opened', { item_type: 'company', item_id: candidate.id, workspace_id: workspaceId })}
          href={candidate.company_website}
          target="_blank"
          rel="noreferrer"
          aria-label={t('Open {name} website', { name: candidate.company_name })}
        >
          <ArrowUpRight size={18} />
        </a>
      </header>

      <div className="radar-candidate-body">
        <section>
          <span className="radar-card-label">{t("Why it may fit you")}</span>
          <p className="radar-fit-summary">{d(candidate.fit_summary)}</p>
          <div className="radar-evidence-stack">
            {evidenceLinks.map((evidence) => (
              <EvidenceLink key={`${evidence.url}-${evidence.excerpt}`} evidence={evidence} candidateId={candidate.id} workspaceId={workspaceId} />
            ))}
          </div>
        </section>

        <section className={isMoment ? 'radar-trigger-block' : 'radar-watch-block'}>
          <span className="radar-card-label">{t("What changed")}</span>
          {candidate.trigger ? (
            <>
              <time>{candidate.trigger.event_date}</time>
              <p>{d(candidate.trigger.summary)}</p>
              {triggerEvidence && <EvidenceLink evidence={triggerEvidence} candidateId={candidate.id} workspaceId={workspaceId} />}
            </>
          ) : (
            <>
              <p>{t("No timely trigger passed our evidence check yet.")}</p>
              <small>{t("This company fits the brief, but timing still needs watching.")}</small>
            </>
          )}
        </section>
      </div>

      {outreachAction}
      <footer className="radar-feedback-area">
        {onWatch && <button type="button" className="radar-secondary-action" disabled={busy || candidate.watch?.status === 'active'}
          aria-label={t('Monitor {name}', { name: candidate.company_name })} onClick={() => onWatch(candidate.id)}>
          <CircleDot size={15}/>{candidate.watch?.status === 'active' ? t("Watching") : candidate.watch ? t("Resume monitoring") : t("Monitor")}
        </button>}
        {candidate.feedback ? (
          <div className={`radar-feedback-saved ${candidate.feedback.verdict}`}>
            <Check size={16} />
            <span>
              {candidate.feedback.verdict === 'relevant' ? t("Marked relevant") : t("Marked not a fit")}
              {candidate.feedback.verdict === 'not_fit' && candidate.watch?.status === 'paused' && t(" · Watch paused; Undo changes feedback only.")}
            </span>
            <button type="button" onClick={() => onUndo(candidate.id)} disabled={busy}>
              <RotateCcw size={14} />{t("Undo")}</button>
          </div>
        ) : (
          <>
            <span>{t("Help the next search learn")}</span>
            <div className="radar-feedback-buttons">
              <button type="button" onClick={() => choose('relevant')} disabled={busy}>
                <Check size={15} />{t("Relevant")}</button>
              <button type="button" onClick={() => choose('not_fit')} disabled={busy}>{t("Not a fit")}</button>
            </div>
          </>
        )}
      </footer>

      {composer && !candidate.feedback && (
        <div className="radar-feedback-composer">
          <div>
            <strong>{composer === 'relevant' ? t("What looks right?") : t("What is off?")}</strong>
            <button type="button" className="radar-text-button" onClick={() => setComposer(null)}>{t("Cancel")}</button>
          </div>
          <div className="radar-reason-chips">
            {reasonOptions.map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={reasons.includes(value) ? 'selected' : ''}
                onClick={() => toggleReason(value)}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <label>
            <span>{t("Anything else?")}{' '}<small>{t("Optional")}</small></span>
            <textarea value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <button
            type="button"
            className="radar-save-feedback"
            disabled={busy}
            onClick={() => {
              onFeedback(candidate.id, composer, reasons, note.trim() || undefined);
              setComposer(null);
            }}
          >{t("Save feedback")}</button>
        </div>
      )}
    </article>
  );
}

export function RadarWorkspaceView({
  workspaceId, currentSearchRunId,
  merchantName,
  brief,
  briefStatus,
  actPanel, briefPanel, versionFilter, researchPanel, monitorPanel, onActContact, watchFocusId,
  activeSection = briefStatus === 'confirmed' ? 'discover' : 'brief',
  onSectionChange,
  hasUnsavedBriefChanges = false,
  onDiscardBrief,
  candidates,
  watches = [], watchSummary = { active_count: 0, limit: 10 }, signalPlan, savingWatch = false, onWatch, onWatchStatus, onFocus, onWatchConfirm, onSignalFeedback,
  searchStatus,
  searchError,
  assumptions,
  reviewCriteria,
  searchesRemaining,
  usage,
  confirming = false,
  startingSearch = false,
  connectionNotice,
  requestError,
  feedbackError,
  savingFeedback = false,
  onReconnect,
  discoverySummary,
  onBriefChange,
  onConfirmBrief,
  onSearch,
  onFeedback,
  onUndoFeedback,
}: Props) {
  const [usageOpen, setUsageOpen] = useState(false);
  const returningFromStripe=usage?.billing?.returning === true;
  useEffect(() => setUsageOpen(returningFromStripe), [workspaceId,returningFromStripe]);
  const usageData: RadarUsageContext = usage || { searchesRemaining };
  const companyBalance = usageData.outcome?.balances.companies;
  const outcomeExpired = usageData.outcome?.status === 'expired' || (!!usageData.outcome && Date.parse(usageData.outcome.expires_at) <= Date.now());
  const outreach=useOutreachFlow();
  const gateFor=(c:RadarCandidate)=>outreach?.eligibility?.find(g=>g.candidate_id===c.id);
  const canPrepare=(c:RadarCandidate)=>!!gateFor(c)&&!gateFor(c)?.reason&&c.feedback?.verdict!=='not_fit';
  const { t, e, locale } = useRadarLocale();
  const { d } = useRadarDisplay();
  const isSearching = startingSearch || searchStatus === 'queued' || searchStatus === 'processing';
  const noSearchAllowance = companyBalance ? companyBalance.remaining === 0 || outcomeExpired : searchesRemaining === 0;
  const canSearch = !isSearching && !savingFeedback && !confirming && !hasUnsavedBriefChanges && !noSearchAllowance;
  const quotaExhausted = !isSearching && noSearchAllowance;
  const exhaustedCopy = usageData.outcome ? (outcomeExpired ? 'Trial ended' : 'Company allowance used') : 'No searches remaining';
  const showBrief = briefStatus === 'needs_review' || activeSection === 'brief';
  const showWatch = !showBrief && activeSection === 'watch';
  const showAct = !showBrief && activeSection === 'act';
  const currentStep = showBrief ? 0 : showAct ? 3 : showWatch ? 2 : 1;
  const contentRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const screen = showBrief ? 'brief' : showAct ? 'act' : showWatch ? 'watch' : 'discover';
  const completedRunId = discoverySummary?.latest_completed_run_id;
  useEffect(() => {
    if (!contentRef.current) return;
    return observeVisible(contentRef.current, () => radarAnalytics.screenViewed(screen, workspaceId));
  }, [screen, workspaceId]);
  useEffect(() => {
    if (screen !== 'discover' || !workspaceId || !completedRunId || !resultsRef.current
      || (candidates.length === 0 && isSearching)) return;
    return observeVisible(resultsRef.current, () => radarAnalytics.resultsViewed({
      workspace_id: workspaceId, search_run_id: completedRunId, collection_count: candidates.length,
      new_count: discoverySummary?.new_count, seen_again_count: discoverySummary?.seen_again_count,
    }));
  }, [screen, workspaceId, completedRunId, candidates.length, isSearching, discoverySummary?.new_count, discoverySummary?.seen_again_count]);
  useEffect(() => {
    if (!isSearching || !workspaceId || !currentSearchRunId) return;
    const changed = () => radarAnalytics.capture('radar_wait_visibility_changed', {
      workspace_id: workspaceId, search_run_id: currentSearchRunId,
      visible: document.visibilityState !== 'hidden' && screen === 'discover',
      elapsed_ms: radarAnalytics.waitElapsed(currentSearchRunId),
      wait_observed: radarAnalytics.waitElapsed(currentSearchRunId) !== undefined,
    });
    changed(); document.addEventListener('visibilitychange', changed);
    return () => document.removeEventListener('visibilitychange', changed);
  }, [isSearching, workspaceId, currentSearchRunId, screen]);
  useEffect(() => {
    if (!showWatch || !watchFocusId) contentRef.current?.scrollIntoView?.({ block: 'start' });
  }, [showBrief, showWatch, showAct, watchFocusId]);

  return (
    <div className="radar-workspace-shell">
      <aside className="radar-workspace-sidebar">
        <a href={RADAR_LOCALES.find(item => item.locale === locale)!.path} className="radar-workspace-brand">
          <img src="/images/mindstream_logomark.png" alt="" />
          <div><strong>Mindstream</strong><span>{t("Buyer Radar")}</span></div>
        </a>
        <nav aria-label={t("Buyer Radar workspace")}>
          <span>{t("Workspace")}</span>
          <button type="button" className={showBrief ? 'active' : ''} aria-current={showBrief ? 'page' : undefined}
            onClick={() => onSectionChange?.('brief')}>
            <BookOpenText size={17} />{t("Market Brief")}</button>
          <button type="button" className={!showBrief && !showWatch && !showAct ? 'active' : ''} aria-current={!showBrief && !showWatch && !showAct ? 'page' : undefined}
            disabled={briefStatus !== 'confirmed' || confirming} onClick={() => onSectionChange?.('discover')}>
            <Compass size={17} />{t("Discover")}</button>
          <button type="button" className={showWatch ? 'active' : ''} aria-current={showWatch ? 'page' : undefined}
            disabled={briefStatus !== 'confirmed' || confirming} onClick={() => onSectionChange?.('watch')}>
            <CircleDot size={17}/>{t("Monitor")}</button>
          <button type="button" className={showAct ? 'active' : ''} aria-current={showAct ? 'page' : undefined}
            disabled={briefStatus !== 'confirmed' || confirming} onClick={() => onSectionChange?.('act')}>
            <Building2 size={17}/>{t("Act")}</button>
        </nav>
        <RadarUsageEntry data={usageData} onOpen={() => setUsageOpen(true)}/>
        <RadarQuickStartLink/>
        <div className="radar-sidebar-foot">
          <span>{t("Private workspace")}</span>
          <strong>{merchantName}</strong>
        </div>
      </aside>

      <main className="radar-workspace-main">
        <header className="radar-workspace-topbar">
          <div>
            <span>{t("Merchant workspace")}</span>
            <strong>{merchantName}</strong>
          </div>
          {researchPanel && <div className="radar-workspace-tools">{researchPanel}</div>}
        </header>

        <div className="radar-workspace-content" ref={contentRef}>
          {requestError && <div className="radar-search-state is-error" role="alert"><p>{e(requestError)}</p>
            {onReconnect && <button type="button" className="radar-text-button" onClick={onReconnect}>{t("Reconnect")}</button>}
          </div>}
          {feedbackError && <div className="radar-search-state is-error" role="alert"><p>{e(feedbackError)}</p></div>}
          {connectionNotice && <div className="radar-connection-notice" role="status">{t(connectionNotice)}</div>}
          {!(showWatch && monitorPanel) && <section className="radar-journey-guide" aria-label={t("Buyer Radar workflow")}>
            {[t("Understand"), t("Discover"), t("Monitor"), t("Act")].map((step, index) => (
              <div
                key={index}
                className={`${index === currentStep ? 'active' : ''} ${index < currentStep && index!==2 ? 'done' : ''}`}
              >
                <span>{index < currentStep && index!==2 ? <Check size={13} /> : index + 1}</span>
                <div><strong>{t(step)}</strong><small>{[t("Shape the brief"), t("Find evidence"), t("Optional monitoring"), t("Reach the buyer")][index]}</small></div>
              </div>
            ))}
          </section>}

          {(showAct || showWatch) && <RadarUsageInline page={showAct?'act':'watch'} data={usageData} onOpen={() => setUsageOpen(true)}/>}
          <div hidden={!showAct}>{actPanel}</div>
          {showAct ? null : showBrief ? (
            <div id="market-brief">
              {briefPanel || <BriefEditor
                brief={brief}
                assumptions={assumptions}
                reviewCriteria={reviewCriteria}
                onChange={onBriefChange}
                onConfirm={onConfirmBrief}
                confirming={confirming}
                confirmed={briefStatus === 'confirmed'}
                onDiscard={onDiscardBrief}
              />}
            </div>
          ) : showWatch ? (monitorPanel ?? <RadarWatchPanel workspaceId={workspaceId} watches={watches} summary={watchSummary} plan={signalPlan}
            focusedWatchId={watchFocusId}
            busy={savingWatch || savingFeedback || confirming} onStatus={onWatchStatus} onFocus={onFocus} onConfirm={onWatchConfirm} onSignalFeedback={onSignalFeedback}
            onActContact={onActContact} onDiscover={() => onSectionChange?.('discover')}/>) : (
            <section id="discover" className={`radar-discover-section ${outreach?'is-company-workspace':''}`}>
              {versionFilter}
              <header className="radar-discover-head">
                <div>
                  <span className="radar-workspace-eyebrow">{t("Discover ·")}{' '}{d(brief.target_market)}</span>
                  <h1>{t(outreach?"Companies to contact":"Companies worth a closer look")}</h1>
                  <p>{t(outreach?"Clear signals first. Every matching company is a potential conversation.":"Fit Candidate means the company matches your brief. Buyer Moment adds a timely, source-backed reason to act now.")}</p>
                </div>
                {candidates.length > 0 && (
                  <button
                    type="button"
                    className="radar-secondary-action"
                    onClick={onSearch}
                    disabled={!canSearch}
                    aria-busy={isSearching}
                  >
                    {isSearching ? <LoaderCircle className="spin" size={17} /> : <Search size={17} />}
                    {quotaExhausted ? t(exhaustedCopy) : t("Search again with what we learned")}
                  </button>
                )}
              </header>
              {hasUnsavedBriefChanges && <div className="radar-search-state">
                <div><strong>{t("You have unsaved Brief changes.")}</strong><span>{t("Save them before your next search. Your existing results are still here.")}</span></div>
                <button type="button" className="radar-secondary-action" onClick={() => onSectionChange?.('brief')}>{t("Review changes")}</button>
              </div>}

              <div className="radar-discovery-summary">
                <div>
                  {discoverySummary && discoverySummary.search_count > 0 && <>
                    <strong>{t('{count} companies in your workspace', { count: discoverySummary.total_count })}</strong>
                    <span>{t('Latest completed search: {new} new · {seen} found again. Earlier companies and your feedback stay here.', { new: discoverySummary.new_count, seen: discoverySummary.seen_again_count })}</span>
                  </>}
                </div>
              </div>
              <RadarUsageInline page="discover" data={usageData} onOpen={() => setUsageOpen(true)}/>

              {isSearching && (
                <div className="radar-search-state" role="status">
                  <LoaderCircle className="spin" size={20} />
                  <div>
                    <strong>{startingSearch ? t("Starting your search…") : t("Scanning for evidence-backed buyers…")}</strong>
                    <span>{t("This can take about a minute. Your previous results stay visible while this runs.")}</span>
                  </div>
                </div>
              )}

              {searchStatus === 'failed' && (
                <div className="radar-search-state is-error" role="alert">
                  <div>
                    <strong>{t("The latest scan did not finish.")}</strong>
                    <span>{searchError ? e(searchError) : t("Your previous results are still available.")}</span>
                  </div>
                  <button type="button" disabled={!canSearch} onClick={onSearch}>{quotaExhausted ? t(exhaustedCopy) : t("Try again")}</button>
                </div>
              )}

              {outreach&&<div className="radar-discover-outreach">
                <div><p>{t('Select companies to generate Gmail drafts. Clear signals help you prioritize; matching companies can be contacted too.')}</p></div>
                <button className="radar-secondary-action" onClick={()=>onSectionChange?.('act')}>{t('View all contacts')}</button>
                {outreach.eligibilityLimited&&<p role="status">{t('Preparation eligibility is limited to the 1,000 most recent discovery records. Older records remain unavailable for preparation here.')}</p>}
                {!outreach.eligibility&&<p role="status">{t('Checking contact history before preparation…')}</p>}
                {outreach.notice&&<p role="status">{t(outreach.notice)}</p>}
                {outreach.batches.filter(b=>b.delivery&&b.delivery.status!=='completed').slice(0,3).map(b=><p key={b.id} className="radar-batch-progress">{t(b.delivery?.status==='blocked'?'Gmail saving needs attention':'Preparing and saving in the background')} · {b.items.filter(i=>i.draft?.exported).length} / {b.items.length} {t('saved')} <button className="radar-text-button" onClick={()=>onSectionChange?.('act')}>{t('View progress')}</button></p>)}
              </div>}
              <div ref={resultsRef}>
              {candidates.length === 0 && !isSearching ? (
                <div className="radar-empty-state">
                  <div className="radar-orbit"><Search size={24} /></div>
                  <span className="radar-workspace-eyebrow">{t("Brief confirmed")}</span>
                  <h2>{searchStatus === 'ready' ? t("No companies passed the evidence check this time.") : t("Ready to build your first radar.")}</h2>
                  <p>{t("We will search for companies that fit your channel requirements, then keep only claims supported by readable source evidence.")}</p>
                  <button type="button" disabled={!canSearch} className="radar-primary-action" onClick={onSearch}>
                    {quotaExhausted ? t(exhaustedCopy) : searchStatus === 'ready' ? t("Search again") : t("Find my first buyers")} <ChevronRight size={18} />
                  </button>
                </div>
              ) : (
                outreach?<RadarCompanyTable candidates={candidates} eligible={canPrepare}
                  automatic={outreach.gmailAvailable} selectionRevision={outreach.selectionRevision}
                  identity={c=>gateFor(c)?.company_key||c.id}
                  progress={c=>{
                    const item=outreach?.batches.flatMap(b=>b.items).find(i=>i.company_key===gateFor(c)?.company_key);
                    if(item?.draft?.contact_status==='replied')return 'Replied';
                    if(item?.draft?.contact_status==='sent')return 'Sent';
                    if(item?.draft?.exported)return 'Saved to Gmail';
                    if(item?.delivery_state==='skipped')return 'Excluded from this batch';
                    if(item?.delivery_state)return 'Needs attention';
                    return canPrepare(c)?'Not started':preparationReasons[gateFor(c)?.reason||'']||'Checking contact history…';
                  }} onPrepare={outreach?.prepare}
                  renderDetail={candidate=><CandidateCard key={candidate.id} workspaceId={workspaceId}
                    candidate={{...candidate,rank:candidates.indexOf(candidate)+1}}
                    outreachAction={outreach&&gateFor(candidate)?.reason&&<div className="radar-candidate-outreach"><span>{t(preparationReasons[gateFor(candidate)?.reason||'']||'Checking contact history…')}</span><button className="radar-text-button" onClick={()=>outreach.focus(gateFor(candidate)!.company_key)}>{t('View progress')}</button></div>}
                    onFeedback={onFeedback} onUndo={onUndoFeedback} onWatch={onWatch} busy={savingFeedback||savingWatch||startingSearch||confirming}/>}/>:<div className="radar-candidate-list">{candidates.map((candidate,index)=><CandidateCard key={candidate.id} workspaceId={workspaceId} candidate={{...candidate,rank:index+1}} onFeedback={onFeedback} onUndo={onUndoFeedback} onWatch={onWatch} busy={savingFeedback||savingWatch||startingSearch||confirming}/>)}</div>

              )}
              </div>

            </section>
          )}
        </div>
      </main>
      <RadarUsageDrawer open={usageOpen} data={usageData} onClose={() => setUsageOpen(false)} onReview={briefStatus==='confirmed' ? () => {setUsageOpen(false);onSectionChange?.('discover');} : undefined}/>
    </div>
  );
}
